/*
 * Yurls Shop — "laad meer" met voortgangsbalk.
 *
 * Haalt de volgende pagina op, plakt de kaartjes onder de bestaande en werkt
 * de teller en de balk bij. Zonder JavaScript blijft de knop een gewone link
 * naar pagina 2, dus de collectie is altijd volledig te doorlopen — ook voor
 * zoekmachines en bij een trage verbinding.
 *
 * Verwachte opbouw:
 *   <yurls-load-more data-next-url="..." data-total="62">
 *     <ul data-grid> ...kaartjes... </ul>
 *     <span data-count></span>
 *     <div data-bar><div data-fill></div></div>
 *     <a data-load href="...">Laad meer</a>   <-- ontbreekt als alles past
 *   </yurls-load-more>
 *
 * Let op: de knop is optioneel. Past de hele collectie op één pagina, dan
 * rendert Liquid geen knop. De teller en de balk moeten het dan nog steeds
 * doen, dus dit bestand mag niet afhaken zodra de knop ontbreekt.
 */

if (!customElements.get('yurls-load-more')) {
  customElements.define(
    'yurls-load-more',
    class YurlsLoadMore extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-grid]');
        this.button = this.querySelector('[data-load]');
        this.countEl = this.querySelector('[data-count]');
        this.fillEl = this.querySelector('[data-fill]');

        // Zonder raster valt er niets te tellen of te laden.
        if (!this.grid) return;

        this.total = parseInt(this.dataset.total, 10) || this.grid.children.length;
        this.loading = false;

        if (this.button) {
          this.onClick = (event) => {
            // Zonder next-url is de knop uitgeput; laat de link dan met rust.
            if (!this.dataset.nextUrl) return;
            event.preventDefault();
            this.loadNext();
          };
          this.button.addEventListener('click', this.onClick);
        }

        // Ook zonder knop: teller en balk meteen invullen.
        this.update();
      }

      disconnectedCallback() {
        if (this.button && this.onClick) this.button.removeEventListener('click', this.onClick);
      }

      async loadNext() {
        if (this.loading || !this.dataset.nextUrl) return;
        this.loading = true;
        this.setAttribute('data-loading', '');
        if (this.button) this.button.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(this.dataset.nextUrl);
          if (!response.ok) throw new Error('Kon de volgende pagina niet ophalen');

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const nextGrid = html.querySelector('[data-grid]');
          const nextHost = html.querySelector('yurls-load-more');

          if (nextGrid) {
            // appendChild verplaatst het element, dus de lijst krimpt tijdens
            // het lopen — vandaar een kopie van de kinderen vooraf.
            Array.from(nextGrid.children).forEach((child) => this.grid.appendChild(child));
          }

          const nextUrl = nextHost ? nextHost.dataset.nextUrl : '';
          if (nextUrl) {
            this.dataset.nextUrl = nextUrl;
            if (this.button) this.button.href = nextUrl;
          } else {
            delete this.dataset.nextUrl;
            if (this.button) this.button.removeAttribute('href');
          }
        } catch (error) {
          // Bij een fout blijft de knop een werkende link naar de volgende
          // pagina; de bezoeker komt er dus hoe dan ook.
          console.warn('[yurls-load-more]', error);
          this.setAttribute('data-error', '');
        } finally {
          this.loading = false;
          this.removeAttribute('data-loading');
          if (this.button) this.button.removeAttribute('aria-busy');
          this.update();
        }
      }

      update() {
        const shown = this.grid.children.length;
        const done = !this.dataset.nextUrl;

        if (this.countEl) {
          const template = this.countEl.dataset.template || '[getoond] van [totaal] producten';
          this.countEl.textContent = template
            .replace('[getoond]', shown)
            .replace('[totaal]', this.total);
        }

        if (this.fillEl) {
          const percentage = this.total > 0 ? Math.round((shown / this.total) * 100) : 100;
          this.fillEl.style.width = Math.min(100, percentage) + '%';
        }

        this.toggleAttribute('data-complete', done);

        if (done && this.button) {
          this.button.setAttribute('aria-disabled', 'true');
          const doneLabel = this.button.dataset.doneLabel;
          if (doneLabel) this.button.textContent = doneLabel;
        }
      }
    }
  );
}
